import { useEffect, useState } from 'react';
import { Search, ShieldCheck } from 'lucide-react';
import { useAdminUsers, ADMIN_PAGE_SIZE } from '../../hooks/useAdminData';
import { useTranslation } from '../../context/LanguageContext';
import { PLAN_NAME_KEYS } from '../../i18n';
import { AdminPanel, AdminStateRow, Pager, formatDate, formatNumber } from './AdminShared';

export function AdminUsersTab({
  onViewActivity,
}: Readonly<{ onViewActivity: (userId: string) => void }>) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { data, loading, error } = useAdminUsers(page, search);
  const users = data?.items ?? [];

  return (
    <AdminPanel
      title={t('nav.adminUsers')}
      actions={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('admin.searchUsers')}
            className="w-56 rounded-lg border border-slate-700 bg-slate-900 py-1.5 pl-8 pr-3 text-xs text-slate-200 placeholder:text-slate-500 focus:border-violet-500 focus:outline-none"
          />
        </div>
      }
    >
      <AdminStateRow
        loading={loading}
        error={error}
        empty={users.length === 0}
        emptyLabel={t('admin.noUsers')}
      />

      {!loading && !error && users.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-700 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-2.5 font-bold">{t('admin.colUser')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colPlan')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colSignedUp')}</th>
                  <th className="px-3 py-2.5 font-bold">{t('admin.colLastSeen')}</th>
                  <th className="px-3 py-2.5 font-bold text-right">{t('admin.colGenerations')}</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-700/40 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-white truncate flex items-center gap-1.5">
                            {u.displayName ?? u.email ?? u.id.slice(0, 8)}
                            {u.role === 'superadmin' && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-bold text-violet-300">
                                <ShieldCheck className="h-2.5 w-2.5" />
                                {t('admin.roleBadge')}
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-slate-500 truncate">{u.email ?? '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {u.plan ? (
                        <span className="text-xs text-slate-300">{t(PLAN_NAME_KEYS[u.plan])}</span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                      {u.status && (
                        <p className="text-[10px] text-slate-500">{t(`status.${u.status}`)}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {u.lastSignInAt ? formatDate(u.lastSignInAt) : t('admin.never')}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="text-xs font-bold text-violet-400">
                        {formatNumber(u.totalGenerations)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onViewActivity(u.id)}
                        className="text-xs font-semibold text-violet-400 hover:text-violet-300 whitespace-nowrap transition-colors"
                      >
                        {t('admin.viewActivity')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={page}
            pageSize={ADMIN_PAGE_SIZE}
            total={data?.total ?? 0}
            onChange={setPage}
          />
        </>
      )}
    </AdminPanel>
  );
}
